import React, { useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';
import wireframeMenuLayoutColumn from '../../assets/icons/wireframeMenuLayoutColumn.svg';
import wireframeMenuLayoutRow from '../../assets/icons/wireframeMenuLayoutRow.svg';
import { svgToIcon } from '../../utils/svg';
import {
  BaseTool,
  type IToolActionInput,
  type IToolAvailableInput,
  type ToolButtonComponent,
} from '../../tools/Tool';
import ToolWireframe from './ToolWireframe';
import ToolButton from '../../toolbars/ToolButton';
import { type Instance as TippyInstance } from 'tippy.js/index';
import { isWireframe } from '../../objects/guards';
import ToolbarMenu from '../../toolbars/ToolbarMenu';
import ToolbarRow from '../../toolbars/ToolbarRow';
import { WIREFRAME_SELECTION_ERROR_MESSAGE } from '../../utils/tools';

interface PopupProps {
  currentLayout: string;
  setCurrentLayout: (layout: string) => void;
  hidePopup: () => void;
}

const LayoutOptionButton = (tool: typeof ToolWireframeLayoutOption, icon) => {
  const Button: React.FC<{
    currentLayout: string;
    setLayout: (tool: typeof ToolWireframeLayoutOption) => void;
    hidePopup: () => void;
  }> = ApplicationView(({ currentLayout, setLayout, hidePopup }) => (
    <ToolButton
      active={tool.layout === currentLayout}
      onClick={() => {
        setLayout(tool);
        hidePopup();
      }}
      tool={tool}
    >
      {svgToIcon(icon)}
    </ToolButton>
  ));
  return Button;
};

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ currentLayout, setCurrentLayout, hidePopup }) => {
    const controller = useController(WhiteboardController);
    const layoutButtons = [
      ToolWireframeLayoutRow.buttonComponent(),
      ToolWireframeLayoutColumn.buttonComponent(),
    ];

    const setLayout = tool => {
      controller.actionApplyTool(tool);
      setCurrentLayout(tool.layout);
      hidePopup();
    };

    return (
      <ToolbarMenu columns={2}>
        <ToolbarRow>
          {layoutButtons.map((Button, index) => (
            <Button
              key={index}
              currentLayout={currentLayout}
              setLayout={setLayout}
              hidePopup={hidePopup}
            />
          ))}
        </ToolbarRow>
      </ToolbarMenu>
    );
  }
);

const LayoutButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const tippyRef = useRef<TippyInstance>();
    const layout = selection.firstObject().layout;
    const [currentLayout, setCurrentLayout] = useState(layout);
    const icon =
      currentLayout === 'row'
        ? wireframeMenuLayoutRow
        : wireframeMenuLayoutColumn;

    return (
      <ToolButton
        popup={
          <Popup
            currentLayout={currentLayout}
            setCurrentLayout={setCurrentLayout}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tool={tool}
      >
        {svgToIcon(icon)}
      </ToolButton>
    );
  }
);

class ToolWireframeLayout extends ToolWireframe {
  static toolName = 'Layout';

  static enabled({ selection }: { controller: any; selection: any }): boolean {
    return selection.type === 'wireframe';
  }

  static available({ selection }: IToolAvailableInput) {
    return selection?.type === 'wireframe';
  }

  static buttonComponent() {
    return LayoutButton;
  }
}

class ToolWireframeLayoutOption extends BaseTool {
  static layout: string;

  static action({ selection }: IToolActionInput) {
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    selection.set('layout', this.layout);
  }
}

class ToolWireframeLayoutRow extends ToolWireframeLayoutOption {
  static toolName = 'Horizontal';
  static layout = 'row';

  static buttonComponent() {
    return LayoutOptionButton(ToolWireframeLayoutRow, wireframeMenuLayoutRow);
  }
}

class ToolWireframeLayoutColumn extends ToolWireframeLayoutOption {
  static toolName = 'Vertical';
  static layout = 'column';

  static buttonComponent() {
    return LayoutOptionButton(
      ToolWireframeLayoutColumn,
      wireframeMenuLayoutColumn
    );
  }
}

export default ToolWireframeLayout;
